import { FloppyDiskIcon } from '@phosphor-icons/react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { uploadFileToS3 } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useGcrsDetails } from '../api';
import { GcrForm } from '../components';
import { GcrFormValues } from '../validations';

export const GcrDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const {
    details,
    uploadUrlDataMutation,
    isFetching,
    updateGcrMutation,
    optionsData,
  } = useGcrsDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    const [firstCombination] = details.combinations;

    return {
      name: details.name,
      colorId: firstCombination?.color.id ?? '',
      printerId: firstCombination?.printer.id ?? '',
      filePublicUrl: details.fileObject?.readUrl ?? '',
      combinations: details.combinations.map((combination) => ({
        typeId: combination.type.id,
        sizeId: combination.size.id,
      })),
    };
  }, [details]);

  const updateGcrFile = async (values: GcrFormValues) => {
    try {
      let key: string | null | undefined;

      if (values.filePublicUrl instanceof File) {
        const { url, key: uploadKey } =
          await uploadUrlDataMutation.mutateAsync();
        await uploadFileToS3({ file: values.filePublicUrl, uploadUrl: url });
        key = uploadKey;
      } else if (!values.filePublicUrl && defaultValues?.filePublicUrl) {
        key = null;
      }

      await updateGcrMutation.mutateAsync({
        id: params.id,
        name: values.name,
        combinations: values.combinations.map((combination) => ({
          colorId: values.colorId,
          printerId: values.printerId,
          typeId: combination.typeId,
          sizeId: combination.sizeId,
        })),
        ...(key !== undefined && { key }),
      });
      toast.success(`Gcr “${values.name}” updated successfully!`);
    } catch (error) {
      toast.error(error?.message || 'Uh oh! Something went wrong.');
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <GcrForm
        options={optionsData}
        onSubmit={updateGcrFile}
        defaultValues={defaultValues}
        fileObject={details?.fileObject}
      >
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Gcrs}
          actions={[
            <Button
              key="save-gcr-file"
              type="submit"
              data-testid="save-gcr-file"
              isLoading={
                updateGcrMutation.isPending || uploadUrlDataMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </GcrForm>
    </LoadingContainer>
  );
};
